import React, { useEffect, useState } from 'react';
import { addCommas, digitsFaToEn } from '@persian-tools/persian-tools';
import styles from './PriceRangeMobile.module.scss';
import Icon from '@/components/UI/icon/Icon';
import Box from '@/components/UI/box/Box';
import Button from '@/components/UI/button/Button';
import TextField from '@/components/UI/textField/TextField';
import Slider from '@/components/UI/slider/Slider';
import Typography from '@/components/UI/typography/Typography';
import { useRouter } from 'next/router';
type IPriceRangeMobile = {
  value: number[];
  onClose: () => void;
  setValue: (arg: number[]) => void;
  step: number;
  min: number;
  max: number;
  submitData: (nextRange?: number[]) => void;
  resetFilter: (e: any) => void;
  hasPriceRangeFilter: boolean;
};
const PriceRangeMobile = ({
  value,
  onClose,
  setValue,
  step,
  min,
  max,
  submitData,
  resetFilter
}: IPriceRangeMobile) => {
  const router = useRouter();
  const [minText, setMinText] = useState(addCommas(value[0]));
  const [maxText, setMaxText] = useState(addCommas(value[1]));
  useEffect(() => {
    setMinText(addCommas(value[0]));
    setMaxText(addCommas(value[1]));
  }, [value]);
  const sanitize = (raw: string) => String(digitsFaToEn(raw)).replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '').slice(0, String(max).length);
  const normalize = (raw: string, fallback: number) => {
    const digits = sanitize(raw);
    if (!digits) return fallback;
    const rounded = Math.round(Number(digits) / 1000) * 1000;
    return Math.min(max, Math.max(min, rounded));
  };
  const resolveRange = (minRaw: string, maxRaw: string): number[] => {
    let low = normalize(minRaw, min);
    let high = normalize(maxRaw, max);
    if (low > high) [low, high] = [high, low];
    return [low, high];
  };
  const handleBoundChange = (raw: string, bound: 'min' | 'max') => {
    const digits = sanitize(raw);
    const isMin = bound === 'min';
    if (!digits) {
      if (isMin) setMinText('');else setMaxText('');
      return;
    }
    const amount = Math.min(Number(digits), max);
    const formatted = addCommas(amount);
    if (isMin) setMinText(formatted);else setMaxText(formatted);
    const other = isMin ? value[1] : value[0];
    if (amount >= min && (isMin ? amount <= other : amount >= other)) {
      setValue(isMin ? [amount, other] : [other, amount]);
    }
  };
  const commitRange = (minRaw = minText, maxRaw = maxText) => {
    const nextRange = resolveRange(minRaw, maxRaw);
    setMinText(addCommas(nextRange[0]));
    setMaxText(addCommas(nextRange[1]));
    setValue(nextRange);
    return nextRange;
  };
  return <Box className={styles['price-range-mobile-container']} data-sentry-element="Box" data-sentry-component="PriceRangeMobile" data-sentry-source-file="PriceRangeMobile.tsx">
      <Box className={styles['price-range-header']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
        <Box onClick={onClose} className={styles['close-icon-section']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
          <Icon name="close" type="highEmphasis" size={'large'} data-sentry-element="Icon" data-sentry-source-file="PriceRangeMobile.tsx" />
        </Box>
        <Typography variant="title_Bold" data-sentry-element="Typography" data-sentry-source-file="PriceRangeMobile.tsx">
          {router.query?.mid_term ? router.query?.checkin && router.query?.checkout ? 'قیمت کل' : 'قیمت ماهانه' : 'قیمت برای هر شب'}
        </Typography>
      </Box>
      <Box className={styles['price-range-section']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
        <Box className={styles['price-range-input-section']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
          <Box className={styles['price-range-input']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
            <Typography variant="bodyL_Bold" data-sentry-element="Typography" data-sentry-source-file="PriceRangeMobile.tsx">از</Typography>
            <TextField type="tel" inputMode="numeric" label="حداقل قیمت" onChange={e => handleBoundChange(e.target.value, 'min')} onBlur={() => commitRange()} onClear={() => {
            setMinText(addCommas(min));
            setValue([min, value[1]]);
          }} value={minText} endAdornment="تومان" data-sentry-element="TextField" data-sentry-source-file="PriceRangeMobile.tsx" />
          </Box>
          <Box className={styles['price-range-input']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
            <Typography variant="bodyL_Bold" data-sentry-element="Typography" data-sentry-source-file="PriceRangeMobile.tsx">تا</Typography>
            <TextField type="tel" inputMode="numeric" label="حداکثر قیمت" onChange={e => handleBoundChange(e.target.value, 'max')} onBlur={() => commitRange()} onClear={() => {
            setMaxText(addCommas(max));
            setValue([value[0], max]);
          }} value={maxText} endAdornment="تومان" data-sentry-element="TextField" data-sentry-source-file="PriceRangeMobile.tsx" />
          </Box>
        </Box>
        <Box className={styles['price-range-slider']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
          <Slider value={value} onChange={newValue => {
          setValue(newValue as number[]);
        }} min={min} max={max} step={step} data-sentry-element="Slider" data-sentry-source-file="PriceRangeMobile.tsx" />
          <Box className={styles['min-max-price-text']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
            <Typography variant="bodyL" data-sentry-element="Typography" data-sentry-source-file="PriceRangeMobile.tsx">گران‌ترین</Typography>
            <Typography variant="bodyL" data-sentry-element="Typography" data-sentry-source-file="PriceRangeMobile.tsx">ارزان‌ترین</Typography>
          </Box>
        </Box>
      </Box>
      <Box className={styles['price-range-button']} data-sentry-element="Box" data-sentry-source-file="PriceRangeMobile.tsx">
        <Button format="text" onClick={resetFilter} data-sentry-element="Button" data-sentry-source-file="PriceRangeMobile.tsx">
          پاک کردن
        </Button>
        <Button format="primary" onClick={() => submitData(commitRange())} data-sentry-element="Button" data-sentry-source-file="PriceRangeMobile.tsx">
          اعمال بازه قیمتی
        </Button>
      </Box>
    </Box>;
};
export default PriceRangeMobile;